import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Grid, Typography } from '@mui/material';
import { di } from '../../../state/inversify.config';
import styles from './playerList.module.scss';
import { CreateIncidentDomainService } from '../createIncidentDomainService';
import { ReportIncidentDumb } from '../dumb/reportIncident.dumb';
import { getRouteByAccountType } from '../../../routes/getRouteByAccountType';
import { Player } from '../../../state/reduxRepositories/players/player.interfaces';
import { IncidentListDomainService } from '../../incidentsList/incidentListDomainService';
import { AuthService } from '../../../state/helperServices/auth/auth.service';
import { PlayerCardNameOnly } from '../../../components/playerCardNameOnly/playerCardNameOnly';
import { AccountTypes } from '../../../components/changeUser/accountTypes.enum';

export function PlayerListSmart() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const createIncidentDomainService = di.get<CreateIncidentDomainService>(
    CreateIncidentDomainService,
  );
  const activeStep = createIncidentDomainService.useActiveStepSelector();

  // TODO: break this out into createIncidentDomainService but reuse code across all places that needs open incidents
  const incidentListDomain = di.get<IncidentListDomainService>(
    IncidentListDomainService,
  );
  const openIncidents = incidentListDomain.useOpenIncidentSelector();

  // TODO: move into a domainService and only return accountType or even better find a way so that you don't have to send down the accountType
  const authService = di.get<AuthService>(AuthService);
  const loggedInUser = authService.useLoggedInUserSelector();
  const isOrgUser = loggedInUser?.accountType === AccountTypes.Org;

  const allPlayers = createIncidentDomainService.usePlayerResourceSelector();
  const teamPlayers = isOrgUser
    ? createIncidentDomainService.useOrgTeamPlayerSelector()
    : [];

  const players = isOrgUser ? teamPlayers : allPlayers;

  const noPlayerMessage =
    !players.length && isOrgUser
      ? t('NO_TEAM_OR_PLAYERS')
      : t('NO_PLAYERS_IN_YOUR_FAMILY');

  const onRedirectToHome = () => {
    createIncidentDomainService.dispatchResetStep();
    navigate(`/${getRouteByAccountType(loggedInUser?.accountType)}`);
  };

  const onSelectPlayer = (selectedPlayerId: string) => {
    createIncidentDomainService.dispatchQueryParams({
      selectedPlayerId,
    });

    createIncidentDomainService.dispatchNextPageQuery();
  };

  return (
    <ReportIncidentDumb
      symptomQuestion={t('incident.INCIDENT_REPORT_WHICH_PLAYER')}
      activeStep={activeStep}
      totalStep={4}
      onCloseReportIncidentPage={onRedirectToHome}
    >
      {!players.length && (
        <Typography variant="h6" sx={{ m: 1 }}>
          {noPlayerMessage}
        </Typography>
      )}
      {players.map((player: Player) => {
        return (
          <Grid container key={player.id} sx={{ mt: 1 }}>
            {openIncidents.filter(
              (openIncident) => openIncident.playerId === player.id,
            )?.length <= 0 ? (
              <Grid
                item
                xs={8}
                onClick={() => onSelectPlayer(player.id)}
                sx={{ mb: 1 }}
              >
                <PlayerCardNameOnly player={player} />
              </Grid>
            ) : (
              <Grid
                item
                xs={8}
                className={styles.withExistingIncident}
                sx={{ mb: 1 }}
              >
                <PlayerCardNameOnly player={player} />
              </Grid>
            )}
          </Grid>
        );
      })}
    </ReportIncidentDumb>
  );
}
